import { useEffect, useMemo, useState } from "react";
import { Search, ShieldCheck, Ban, ChevronDown, Check, X } from "lucide-react";
import { api } from "@/lib/api";

const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

const boldLead = (t) => {
  const m = t.match(/^([A-Z][^:]{1,40}):\s*(.+)$/);
  if (m) return (<><span className="font-semibold text-bone">{m[1]}:</span> {m[2]}</>);
  return t;
};

const PolicySection = ({ s }) => {
  const body = lines(s.body);
  if (s.kind === "denylist") {
    const [intro, ...items] = body.length && body[0].endsWith(":") ? body : ["", ...body];
    return (
      <div className="scroll-mt-24">
        <h3 className="font-feature text-sm uppercase tracking-[0.2em] text-ember mb-3">{s.heading}</h3>
        {intro && <p className="font-body text-[15px] leading-relaxed text-[#c8c0b4] mb-4">{intro}</p>}
        <div className="space-y-2.5">
          {items.map((it, i) => (
            <div key={i} className="flex items-start gap-3 border-l-2 border-ember/50 bg-ember/5 px-4 py-3">
              <X className="h-4 w-4 text-ember shrink-0 mt-0.5" /><span className="text-[#c8c0b4]">{it}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="scroll-mt-24">
      <h3 className="font-feature text-sm uppercase tracking-[0.2em] text-teal mb-3">{s.heading}</h3>
      <div className="space-y-4">
        {body.map((p, i) => <p key={i} className="font-body text-[15px] leading-relaxed text-[#c8c0b4]">{boldLead(p)}</p>)}
      </div>
    </div>
  );
};

export const OwnershipPolicy = () => {
  const [secs, setSecs] = useState([]);
  useEffect(() => { api.get("/biz-policy").then(({ data }) => setSecs(data.filter((x) => x.group === "ownership"))).catch(() => {}); }, []);
  return (
    <div className="space-y-12" data-testid="ownership-policy">
      {secs.map((s) => <PolicySection key={s.id} s={s} />)}
    </div>
  );
};

const BizCard = ({ b, open, onToggle }) => (
  <div className="border border-[#2A201A] bg-surface/40" data-testid={`biz-sales-${b.order}`}>
    <button type="button" onClick={onToggle} aria-expanded={open}
      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-surface/70 transition-colors">
      <span className="font-display text-xl font-light text-bone">{b.business}</span>
      <ChevronDown className={`h-4 w-4 text-teal shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <div className="px-5 pb-5 pt-1 grid gap-6 sm:grid-cols-2">
        <div>
          <p className="flex items-center gap-2 font-feature text-xs uppercase tracking-[0.2em] text-teal mb-3"><ShieldCheck className="h-4 w-4" /> May Sell</p>
          <ul className="space-y-2">
            {lines(b.may_sell).map((it, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-[#c8c0b4]"><Check className="h-4 w-4 text-teal shrink-0 mt-0.5" />{it}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="flex items-center gap-2 font-feature text-xs uppercase tracking-[0.2em] text-ember mb-3"><Ban className="h-4 w-4" /> Cannot Sell</p>
          <ul className="space-y-2">
            {lines(b.cannot_sell).map((it, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-[#c8c0b4]"><X className="h-4 w-4 text-ember shrink-0 mt-0.5" />{it}</li>
            ))}
          </ul>
        </div>
        {b.notes && (
          <div className="sm:col-span-2 border-l-2 border-gold/50 bg-gold/5 px-4 py-3 text-sm text-[#c9c0b0]">
            <span className="font-feature text-[10px] uppercase tracking-wider text-gold mr-2">Notes</span>{b.notes}
          </div>
        )}
      </div>
    )}
  </div>
);

export const SalesGuide = () => {
  const [intro, setIntro] = useState([]);
  const [biz, setBiz] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [showIntro, setShowIntro] = useState(false);
  useEffect(() => {
    api.get("/biz-policy").then(({ data }) => setIntro(data.filter((x) => x.group === "sales_intro"))).catch(() => {});
    api.get("/biz-sales").then(({ data }) => setBiz(data)).catch(() => {});
  }, []);
  const s = q.trim().toLowerCase();
  const filtered = useMemo(() => biz.filter((b) =>
    b.business.toLowerCase().includes(s) || b.may_sell.toLowerCase().includes(s) || b.cannot_sell.toLowerCase().includes(s)), [s, biz]);

  return (
    <div data-testid="sales-guide">
      {/* Shared rules (collapsible) */}
      <button onClick={() => setShowIntro((v) => !v)} data-testid="sales-intro-toggle"
        className="w-full flex items-center justify-between border border-[#2A201A] bg-obsidian/50 px-5 py-4 mb-6 hover:border-teal/40 transition-colors">
        <span className="font-feature text-xs uppercase tracking-[0.2em] text-teal">General Sales Rules & Prohibitions</span>
        <ChevronDown className={`h-4 w-4 text-teal transition-transform ${showIntro ? "rotate-180" : ""}`} />
      </button>
      {showIntro && <div className="space-y-10 mb-10">{intro.map((x) => <PolicySection key={x.id} s={x} />)}</div>}

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F877C]" />
        <input data-testid="sales-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a business or item..."
          className="w-full bg-surface/60 border border-[#2A201A] pl-11 pr-4 py-3 text-sm text-bone placeholder:text-[#5c554c] focus:outline-none focus:border-teal transition-colors" />
      </div>

      <div className="space-y-3">
        {filtered.map((b) => <BizCard key={b.id} b={b} open={open === b.id} onToggle={() => setOpen(open === b.id ? null : b.id)} />)}
        {filtered.length === 0 && <p className="py-12 text-center font-mono text-sm text-[#8F877C]">No businesses match your search.</p>}
      </div>
    </div>
  );
};
